import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { ReferenceArrayInput, AutocompleteArrayInput, useGetList, useTranslate } from 'react-admin';
import { Box, Button, Chip, Popper } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckmarkSmallIcon from '@/assets/icons/checkmark-small-icon.svg?react';
import { Palette } from '@/themes/themeOverrides/Palette.js';
import PropTypes from 'prop-types';

const CategoriesInputPopper = props => <Popper {...props} className='popper--categories-input' />;

const StyledCategoriesInput = styled(Box)(({ theme }) => ({
    '& .categories-input': {
        position: 'relative',
        '& .MuiFormHelperText-root': {
            display: 'none',
        },
    },
    '& .MuiAutocomplete-root.ra-input, .MuiFormControl-root': {
        minWidth: '100%',
    },
    '& .MuiOutlinedInput-root': {
        padding: '12px 16px !important',
    },
    '& .categories-input__popup': {
        zIndex: 10,
        position: 'absolute',
        background: Palette.white,
        width: '100%',
        boxShadow: Palette.boxShadow.elevation16,
        borderRadius: '12px',
        display: 'none',
        padding: '0',
        '&.open': {
            display: 'block',
        },
        '& .actions': {
            padding: '24px',
            position: 'sticky',
            bottom: '-1px',
            background: Palette.white,
            borderRadius: '0 0 12px 12px',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            borderTop: `2px solid ${Palette.gray.gray100}`,
            [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
            },
        },
    },
    '& .categories-list': {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        maxHeight: '400px',
        overflowY: 'auto',
        padding: '16px',
    },
}));

export const ProductCategoriesInput = ({ source = 'categoryIds', label, value, onChange, sx }) => {
    const { setValue, control } = useFormContext();
    const translate = useTranslate();
    const rawWatchedValue = useWatch({ control, name: source });
    const watchedValue = useMemo(() => rawWatchedValue || [], [rawWatchedValue]);

    const defaultLabel = label || translate('resources.campaign.labels.productCategories');
    const currentValue = value !== undefined ? value : watchedValue;

    const { data: categories = [], isLoading } = useGetList('product-categories-mock', {
        pagination: { page: 1, perPage: 1000 },
    });

    const [open, setOpen] = useState(false);
    const [tempSelectedIds, setTempSelectedIds] = useState(currentValue);
    const containerRef = useRef(null);
    const popupRef = useRef(null);
    const isInternalUpdate = useRef(false);

    useEffect(() => {
        if (!isInternalUpdate.current) {
            const currentArray = currentValue || [];
            const tempArray = tempSelectedIds || [];

            if (currentArray.length !== tempArray.length || !currentArray.every((id, index) => id === tempArray[index])) {
                setTempSelectedIds([...currentArray]);
            }
        }
        isInternalUpdate.current = false;
    }, [currentValue, tempSelectedIds]);

    useEffect(() => {
        if (value !== undefined) {
            const watchedArray = watchedValue || [];
            if (value.length !== watchedArray.length || !value.every((id, index) => id === watchedArray[index])) {
                setValue(source, value, { shouldDirty: true, shouldValidate: false, shouldTouch: false });
            }
        } else if (onChange) {
            onChange(watchedValue);
        }
    }, [value, watchedValue, onChange, setValue, source]);

    const toggleCategory = id => {
        isInternalUpdate.current = true;
        setTempSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
    };

    const handleToggleAll = () => {
        isInternalUpdate.current = true;
        const allIds = categories.map(c => c.id);
        setTempSelectedIds(prev => (prev.length === allIds.length ? [] : allIds));
    };

    const handleApply = () => {
        if (onChange) {
            onChange(tempSelectedIds);
        } else {
            setValue(source, tempSelectedIds, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
        }
        setOpen(false);
    };

    const handleClickOutside = useCallback(
        event => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setTempSelectedIds(currentValue || []);
                setOpen(false);
            }
        },
        [currentValue],
    );

    const handleCategoryClick = useCallback(
        id => {
            const currentArray = value !== undefined ? value || [] : watchedValue || [];
            const newValue = currentArray.includes(id) ? currentArray.filter(x => x !== id) : [...currentArray, id];

            if (onChange) {
                onChange(newValue);
            } else {
                setValue(source, newValue, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
            }
        },
        [value, watchedValue, onChange, setValue, source],
    );

    useEffect(() => {
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open, handleClickOutside]);

    return (
        <StyledCategoriesInput ref={containerRef} className='autocomplete-parent' sx={sx}>
            <ReferenceArrayInput source={source} reference='product-categories-mock' perPage={1000}>
                <Box className={`categories-input ${open ? 'open' : ''}`}>
                    <Box
                        className='categories-input__trigger'
                        onClick={() => {
                            setOpen(prev => !prev);
                            if (open) {
                                const inputElement = containerRef.current?.querySelector('input');
                                if (inputElement) inputElement.blur();
                            }
                        }}
                    >
                        <AutocompleteArrayInput
                            className={currentValue.length > 0 ? 'input-filled' : ''}
                            fullWidth
                            label={defaultLabel}
                            optionText='name'
                            optionValue='id'
                            PopperComponent={CategoriesInputPopper}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => {
                                    const tagProps = getTagProps({ index });
                                    return (
                                        <Chip
                                            key={option.id}
                                            label={option.name}
                                            size='small'
                                            {...tagProps}
                                            onDelete={e => {
                                                e.stopPropagation();
                                                handleCategoryClick(option.id);
                                            }}
                                            icon={<CheckmarkSmallIcon />}
                                            style={{ backgroundColor: option.colorDark }}
                                        />
                                    );
                                })
                            }
                        />
                    </Box>
                    {open && (
                        <Box className='categories-input__popup open' ref={popupRef}>
                            {isLoading ? (
                                <Box style={{ padding: '16px' }}>{translate('resources.product.labels.loadingCategories')}</Box>
                            ) : (
                                <>
                                    <div className='categories-list'>
                                        {categories.map(category => {
                                            const selected = tempSelectedIds.includes(category.id);
                                            const backgroundColor = selected ? category.colorDark : category.colorLight;
                                            return (
                                                <Chip
                                                    key={category.id}
                                                    label={category.name}
                                                    icon={<CheckmarkSmallIcon style={{ opacity: selected ? 1 : 0.12 }} />}
                                                    size='small'
                                                    onClick={() => toggleCategory(category.id)}
                                                    style={{ backgroundColor }}
                                                />
                                            );
                                        })}
                                    </div>
                                    <Box className='actions'>
                                        <Button variant='contained' color='secondary' onClick={handleToggleAll}>
                                            {tempSelectedIds.length === categories.length
                                                ? translate('resources.product.labels.unselectAll')
                                                : translate('resources.product.labels.selectAll')}
                                        </Button>
                                        <Button variant='contained' color='primary' onClick={handleApply}>
                                            {translate('common.apply')}
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </Box>
                    )}
                </Box>
            </ReferenceArrayInput>
        </StyledCategoriesInput>
    );
};

ProductCategoriesInput.propTypes = {
    source: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.array,
    onChange: PropTypes.func,
    sx: PropTypes.object,
};

export default ProductCategoriesInput;
